import type {
    ComposableOptionsBase,
    AccountNavItem,
    AccountNavItemPermissions,
    AccountNav
} from '~/types';
import {
    useAccountNavData
} from '~/composables';
import { nanoid } from 'nanoid';
import { orderBy as lo_orderBy } from 'es-toolkit';
import { isString as lo_isString } from 'es-toolkit';


export const AccountMenu = () => useState<Nilish<AccountNav>>('keyAccountMenuNavs', () => null);
export const RouteBlackList = () => useState<string[]>('keyRouteBlackList', () => []);


export interface UseAccountNavOptions extends ComposableOptionsBase {
    itemIdPfx: Nilish<string>;
}
export function useAccountNav(options: Partial<UseAccountNavOptions> = {}) {
    const $config = useRuntimeConfig();
    const nuxtApp = useNuxtApp();
    const {
        currentMembershipCtx,
        isOrgMemberAllowed
    } = nuxtApp.$user;
    const {
        accountNavData
    } = useAccountNavData();
    const accountMenu = AccountMenu();
    const routeBlackList = RouteBlackList();
    const accountMenuGroups: AccountNav = accountNavData;

    function verifyPermission(
        rule?: Nilish<Partial<AccountNavItemPermissions> | false>
    ): boolean {
        if (rule === false) return false;
        if (!rule || !Object.keys(rule ?? {}).length) return true;

        let verified = false;

        if (rule.page && isOrgMemberAllowed('page', rule.page)) {
            verified = true;
        } else if (rule.resource && isOrgMemberAllowed('resource', rule.resource, 'view')) {
            verified = true;
        } else if (rule.flag && isOrgMemberAllowed('flag', rule.flag)) {
            verified = true;
        }

        return verified;
    }

    function buildMenu(force?: boolean): AccountNav {
        if (!force && accountMenu.value) {
            return accountMenu.value;
        }

        const pfx = options.itemIdPfx || nanoid(6);
        const featureFlagObj = $config.public.featureFlags;
        routeBlackList.value.splice(0);

        const ctxName = currentMembershipCtx.value;
        const nav: AccountNav = {};
        if (!ctxName) return nav;

        for (const [groupKey, groupObj] of Object.entries(accountMenuGroups)) {
            const subMenu: AccountNavItem[] = [];

            groupObj.items.forEach((item: AccountNavItem) => {
                const { featureFlagKey } = item;
                const isFeatureAllowed = featureFlagKey ? featureFlagObj[featureFlagKey] : true;

                if (!isFeatureAllowed) return;

                const ctxLinkObj = item.link[ctxName];
                if (!ctxLinkObj) return;

                const { permissions: rule } = ctxLinkObj;
                const isVerified = verifyPermission(rule);

                if (ctxLinkObj.to) {
                    // Add to blacklist
                    if (!isVerified) {
                        const to = lo_isString(ctxLinkObj.to) ? ctxLinkObj.to : ctxLinkObj.to.path;

                        if (to) {
                            routeBlackList.value.push(to);
                        }

                        return;
                    }

                    // Add to menu
                    const newItem = Object.assign({}, item);
                    newItem.id = `${pfx}_${ctxName}_${newItem.id}`;
                    newItem.finalLink = ctxLinkObj.to;
                    subMenu.push(newItem);
                }
            });

            if (subMenu.length) {
                nav[groupKey] = {
                    ...groupObj,
                    items: lo_orderBy(subMenu, ['value'], ['asc'])
                };
            }
        }

        accountMenu.value = nav;
        return nav;
    }

    return {
        accountMenu,
        buildMenu
    };
}
